import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { AccountType } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { IBrokerCondition } from 'pages/Admin'
import { pageUrls } from 'pages/Admin/AdminTools/Configuration/FloaterTabs/constants'
import { DocumentsSlideNameType } from 'pages/Loan'
import { DocumentHistoryModal } from 'pages/LoanSubmission/DocumentHistoryModal'
import { DocumentsSlide } from 'pages/LoanSubmission/documents'
import { LoanDetailsSlide } from 'pages/LoanSubmission/loanDetails'
import { NotifyBorrowerSlide } from 'pages/LoanSubmission/NotifyBorrower'
import { LoanSubmissionCondition, LoanSubmissionDocument } from 'pages/LoanSubmission/types'
import { UnderwritingSlide } from 'pages/LoanSubmission/underwriting'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAdminConfig, getLoanSubmission, openS3Document, updateLoanSubmission } from 'services'
import { DocumentFile } from 'stories/components'
import { confirm, formatTime, isLoanLevelPage } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

export function FloaterTabs() {
  const [loading, setLoading] = useState(false)
  const [conditions, setConditions] = useState<LoanSubmissionCondition[]>([])
  const [brokerConditions, setBrokerConditions] = useState<IBrokerCondition[]>([])
  const [currentTemplate, setCurrentTemplate] = useState(-1)
  const [documents, setDocuments] = useState<LoanSubmissionDocument[]>([])
  const [historyDocument, setHistoryDocument] = useState<Record<string, any> | null>(null)
  const [isLoanPage, setIsLoanPage] = useState(false)
  const [floaterTabConfig, setFloaterTabConfig] = useState<Record<string, any>>({})
  const [loanNo, setLoanNo] = useState<string>('0')

  const [auth, loanDetail] = useSelector((state: any) => [state.auth, state.loanDetail])
  const canDeleteDocumentPermanently =
    [AccountType.ADMIN, AccountType.OPERATION_SUPERVISOR, AccountType.UNDERWRITER, AccountType.UW_MANAGER].indexOf(
      auth.profile.accountType,
    ) !== -1

  const { hasPermission } = usePermissions()
  const brokerProfile = !hasPermission('ADMIN_TO_AE_PROFILE_PERMISSION')

  useEffect(() => {
    if (!auth.token) return
    getAdminConfig('floaterTabs').then((res) => {
      setFloaterTabConfig(res)
    })
  }, [auth.token])

  useEffect(() => {
    setIsLoanPage(isLoanLevelPage(location.pathname))
    if (!isLoanLevelPage(location.pathname)) return

    const loanNumber = setLoanNumber()
    setLoanNo(loanNumber)
  }, [location.pathname])

  useEffect(() => {
    if (!isLoanPage || !Number(loanNo)) return

    setLoading(true)
    getLoanSubmission().then(({ templateNumber, conditions, brokerConditions, documents }) => {
      setConditions(conditions)
      setCurrentTemplate(templateNumber)
      setDocuments(documents)
      if (templateNumber == 0) setBrokerConditions(brokerConditions)
      setLoading(false)
    })
  }, [loanNo])

  const onAddDocuments = async (docs: DocumentFile[]) => {
    const newDocs = cloneDeep(documents)
    const addingDocs: LoanSubmissionDocument[] = docs.map((doc) => ({
      conditionNo: 0,
      name: doc.name,
      key: doc.fileKey,
      status: doc.status,
      category: doc.category,
      createdAt: doc.createdAt,
      generated: false,
      createdBy: auth.profile.email,
    }))
    newDocs.push(...addingDocs)
    setDocuments(newDocs)
    setLoading(true)
    await updateLoanSubmission({}, { update: addingDocs })
    setLoading(false)
  }

  const onUpdateDocuments = async (newDocs: Array<any>, updatedDoc: any) => {
    setDocuments(newDocs)
    setLoading(true)
    await updateLoanSubmission({}, { update: [updatedDoc] })
    setLoading(false)
  }

  const onRemoveDocument = async (document: any, isRemoveCompletely: boolean = false) => {
    const content = (
      <div className="text-gray-900 mb-4 text-md">
        {isRemoveCompletely
          ? 'Are you sure want to delete this document permanently?'
          : 'Are you sure want to delete this document?'}
        <div className="text-gray-800 flex flex-col text-left text-sm">
          {document.conditionNo ? <span>Condition No: {document.conditionNo}</span> : null}
          <span>Document Name: {document.name}</span>
          <span>Uploaded Date: {formatTime(new Date(document.createdAt))}</span>
        </div>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    const newData = cloneDeep(documents)
    const index = newData.findIndex((doc) => doc.conditionNo == document.conditionNo && doc.key == document.key)
    if (index == -1) return
    if (isRemoveCompletely) {
      if (document.generated) return
      const removedDocs = newData.splice(index, 1)
      setDocuments(newData)
      setLoading(true)
      await updateLoanSubmission({}, { delete: removedDocs })
      setLoading(false)
    } else {
      newData[index].conditionNo = 0
      setDocuments(newData)
      setLoading(true)
      await updateLoanSubmission({}, { update: [newData[index]] })
      setLoading(false)
    }
  }

  const onRemoveDocuments = async (docKeys: string[]) => {
    const newData = cloneDeep(documents)
    const indexes = docKeys
      .map((key) => newData.findIndex((doc) => doc.key == key))
      .filter((v) => v != -1)
      .sort((a, b) => a - b)
    const removedDocs: Record<string, any>[] = []
    indexes.reverse().forEach((index) => {
      if (newData[index].generated) return
      const items = newData.splice(index, 1)
      removedDocs.push(...items)
    })
    setLoading(true)
    await updateLoanSubmission({}, { delete: removedDocs })

    setDocuments(newData)
    setLoading(false)
  }

  const onOpenDocument = async (document: any) => {
    openS3Document(document.key, true)
  }

  const isVisible = (tab: string, props: string = 'visible') => {
    let rtl = false
    Object.keys(pageUrls).map((key) => {
      if (location.pathname.startsWith(pageUrls[key].url)) {
        if (
          props === 'visible' &&
          floaterTabConfig[tab]?.[key]?.visible &&
          floaterTabConfig[tab]?.accountTypes?.[auth.profile.accountType]
        )
          rtl = true
        if (props === 'editable' && floaterTabConfig[tab]?.[key]?.editableAccTypes?.[auth.profile.accountType])
          rtl = true
      }
    })
    return rtl
  }

  if (!isLoanPage) return <></>
  return (
    <>
      <LayoutLoading show={loading} />
      {isVisible('loanDetail') && <LoanDetailsSlide />}
      {isVisible('notification') && (
        <NotifyBorrowerSlide
          conditions={conditions}
          brokerProfile={brokerProfile}
          brokerConditions={brokerConditions}
          templateNumber={currentTemplate}
          documents={documents}
        />
      )}
      {isVisible('underwriting') && <UnderwritingSlide />}
      {isVisible('documents') && (
        <>
          {isVisible('documents', 'editable') ? (
            <DocumentsSlide
              loading={loading}
              setLoading={setLoading}
              documents={documents}
              onAdd={onAddDocuments}
              onUpdate={onUpdateDocuments}
              onRemove={(document: any) => onRemoveDocument(document, true)}
              onSelectedRemove={(selectedKeys: string[]) => onRemoveDocuments(selectedKeys)}
              onOpen={(document: any) => onOpenDocument(document)}
              onOpenDocHistory={setHistoryDocument}
              brokerProfile={brokerProfile}
              canDeleteDocumentPermanently={canDeleteDocumentPermanently}
              submitToLoanSetup={loanDetail.submitToLoanSetup}
            />
          ) : (
            <DocumentsSlideNameType loadDocs={true} loading={false} position="right" />
          )}
        </>
      )}
      {historyDocument && <DocumentHistoryModal document={historyDocument} onClose={() => setHistoryDocument(null)} />}
    </>
  )
}
